import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  Slider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';

interface ImageScaleSliderProps {
  value: number;
  handleChange: (zoom: number) => void;
  showScaleCheckbox?: boolean;
  scaleCheckboxBool?: boolean;
  handleScaleCheckboxChange?: (scaleCheckboxBool: boolean) => void;
}

const ImageScaleSlider = ({
  value,
  handleChange,
  showScaleCheckbox = true,
  scaleCheckboxBool = true,
  handleScaleCheckboxChange,
}: ImageScaleSliderProps) => {
  const [scale, setScale] = useState(value);
  const [inputValue, setInputValue] = useState(Math.round(value * 100));
  const [simpleScale, setSimpleScale] = useState(showScaleCheckbox);
  const [canScaleBool, setCanScaleBool] = useState(scaleCheckboxBool);

  useEffect(() => {
    setInputValue(Math.round(value * 100));
    setScale(value);
  }, [value]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = Number(event.target.value);
    setInputValue(newValue);
    if (newValue < 10) {
      handleChange(0.1);
    } else if (newValue > 300) {
      handleChange(3);
    } else {
      handleChange(newValue / 100);
    }
  };

  const scaleSlider = useMemo(() => (
    <Slider
      min={0.1}
      max={3}
      step={0.01}
      aria-label="Zoom"
      value={scale}
      sx={{ mx: 2 }}
      onChange={(event, value) => handleChange(value as number)}
    />
  ), [scale]);

  const scaleInput = useMemo(() => (
    <TextField
      size="small"
      inputProps={{
        inputMode: 'numeric',
        pattern: '[0-9]*',
        min: 10,
        max: 300,
        step: 1,
        style: { textAlign: 'right' },
      }}
      sx={{ width: 90 }}
      value={inputValue}
      type="number"
      onChange={handleInputChange}
    />
  ), [inputValue]);

  const scaleContent = useMemo(() => (
    <>
      <Stack spacing={2} direction="row" sx={{ mb: 1 }} alignItems="center">
        Small
        {scaleSlider}
        Large
      </Stack>
      <Box className="d-flex justify-content-end align-items-center mt-1">
        {scaleInput}
        <Typography variant="body1" className="ms-1 text-sm">%</Typography>
      </Box>
    </>
  ), [scaleSlider, inputValue]);

  return (
    <Grid item sx={{ width: '100%' }}>
      {!simpleScale && (
        <>
          <FormControlLabel
            control={(
              <Checkbox
                checked={canScaleBool}
                onClick={(event) => {
                  handleScaleCheckboxChange(!canScaleBool);
                  setCanScaleBool(!canScaleBool);
                }}
                name="parallaxeffect"
                color="primary"
              />
            )}
            label="Image Scale"
          />
          {canScaleBool && scaleContent}
        </>
      )}
      {simpleScale && (
        <>
          <Typography className="col-form-label mb-1 text-sm" sx={{ fontWeight: '700' }}>
            Image Scale
          </Typography>
          {scaleContent}
        </>
      )}
    </Grid>
  );
};

export default ImageScaleSlider;
